import React, { useState } from 'react';
import Button from '../ui/Button';
import apiService from '../../services/ApiService';
import type { Shop } from '../../types/buildExpansion';
import { PlaceLocationEditor } from '../places/PlaceLocationEditor';
import {
  placeLocationFromShop,
  placeLocationToShopPayload,
  type PlaceLocationValue,
} from '../../utils/placeValue';

const SOCIAL_FIELDS = [
  { key: 'instagram', label: 'Instagram' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'x', label: 'X (Twitter)' },
  { key: 'tiktok', label: 'TikTok' },
  { key: 'youtube', label: 'YouTube' },
] as const;

interface ShopProfileEditPanelProps {
  shop: Shop;
  onUpdated: (shop: Shop) => void;
}

/** Owner-editable shop contact and location fields (always editable after save). */
export const ShopProfileEditPanel: React.FC<ShopProfileEditPanelProps> = ({
  shop,
  onUpdated,
}) => {
  const [description, setDescription] = useState(shop.description ?? '');
  const [websiteUrl, setWebsiteUrl] = useState(shop.website_url ?? '');
  const [contactUrl, setContactUrl] = useState(shop.contact_url ?? '');
  const [phone, setPhone] = useState(shop.phone ?? '');
  const [place, setPlace] = useState<PlaceLocationValue>(() => placeLocationFromShop(shop));
  const [social, setSocial] = useState<Record<string, string>>(() => {
    const links = shop.social_links ?? {};
    const out: Record<string, string> = {};
    for (const { key } of SOCIAL_FIELDS) {
      out[key] = typeof links[key] === 'string' ? links[key] : '';
    }
    return out;
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const social_links: Record<string, string> = {};
      for (const { key } of SOCIAL_FIELDS) {
        const value = social[key]?.trim();
        if (value) social_links[key] = value;
      }
      const updated = await apiService.updateShopProfile(shop.id, {
        description: description.trim() || null,
        website_url: websiteUrl.trim() || null,
        contact_url: contactUrl.trim() || null,
        phone: phone.trim() || null,
        social_links,
        ...placeLocationToShopPayload(place),
      });
      onUpdated(updated);
      setPlace(placeLocationFromShop(updated));
      setSaved(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not save shop profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="rounded-xl border border-white/[0.06] bg-[#0c0c0c]/50 p-4 sm:p-5 space-y-5"
      noValidate
    >
      <div>
        <h2 className="text-sm font-semibold text-white">Shop details</h2>
        <p className="text-xs text-gray-500 mt-1">
          Address, phone, and social links appear on your public shop page.
        </p>
      </div>

      {error ? (
        <div role="alert" className="alert-error text-sm">
          {error}
        </div>
      ) : null}
      {saved ? (
        <p className="text-sm text-emerald-300" role="status">
          Shop profile saved.
        </p>
      ) : null}

      <div>
        <label htmlFor="shop-edit-description" className="input-label">
          Description
        </label>
        <textarea
          id="shop-edit-description"
          className="input min-h-[72px]"
          rows={3}
          maxLength={2000}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={saving}
        />
      </div>

      <PlaceLocationEditor
        idPrefix="shop-edit"
        value={place}
        onChange={setPlace}
        disabled={saving}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="shop-edit-phone" className="input-label">
            Phone
          </label>
          <input
            id="shop-edit-phone"
            type="tel"
            className="input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(555) 555-0100"
            disabled={saving}
            autoComplete="tel"
          />
        </div>
        <div>
          <label htmlFor="shop-edit-website" className="input-label">
            Website
          </label>
          <input
            id="shop-edit-website"
            type="url"
            className="input"
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            placeholder="https://"
            disabled={saving}
          />
        </div>
      </div>

      <div>
        <label htmlFor="shop-edit-contact" className="input-label">
          Contact link
        </label>
        <input
          id="shop-edit-contact"
          type="url"
          className="input"
          value={contactUrl}
          onChange={(e) => setContactUrl(e.target.value)}
          placeholder="https://"
          disabled={saving}
        />
      </div>

      <fieldset className="space-y-3">
        <legend className="input-label mb-1">Social media</legend>
        {SOCIAL_FIELDS.map(({ key, label }) => (
          <div key={key}>
            <label htmlFor={`shop-edit-social-${key}`} className="text-xs text-gray-500">
              {label}
            </label>
            <input
              id={`shop-edit-social-${key}`}
              type="url"
              className="input mt-1"
              value={social[key] ?? ''}
              onChange={(e) => setSocial((prev) => ({ ...prev, [key]: e.target.value }))}
              placeholder="https://"
              disabled={saving}
            />
          </div>
        ))}
      </fieldset>

      <div className="flex justify-end">
        <Button type="submit" disabled={saving} className="w-full justify-center sm:w-auto">
          {saving ? 'Saving…' : 'Save profile'}
        </Button>
      </div>
    </form>
  );
};

export default ShopProfileEditPanel;
